import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_URLS } from '../config/api.urls';

export interface SellerListItem {
    id: string;
    sellerType: 'company' | 'private';
    status: 'PENDING' | 'COMPLETED' | 'VERIFIED' | 'REJECTED';
    companyName?: string;
    firstName?: string;
    lastName?: string;
    paymentInfo?: {
        paymentType: 'MOBILE_MONEY' | 'BANK';
        mobileMoneyProvider?: string;
        bankName?: string;
    };
}

export interface CompanySellerDetails {
    id: string;
    status: 'PENDING' | 'COMPLETED' | 'VERIFIED' | 'REJECTED';
    sellerType: 'company';
    companyName: string;
    legalInfo: {
        rccm: string;
        niu: string;
        businessLicenseNumber: string;
        liDocPath: string;
        verificationStatus: string;
    };
    legalRepresentative: {
        fullName: string;
        position: string;
        phoneNumber: string;
        lrDocPath: string;
        verificationStatus: string;
    };
    operationalInfo: {
        companyEmail: string;
        companyPhoneNumber: string;
        country: string;
        city: string;
        address: string;
        description: string;
    };
    paymentInfo: {
        id: string;
        paymentType: 'MOBILE_MONEY' | 'BANK';
        bankName: string | null;
        accountNumber: string | null;
        accountHolderName: string | null;
        mobileMoneyProvider: string | null;
        mobileMoneyNumber: string | null;
        numberVerified: boolean;
    };
    documents: {
        id: string;
        documentPath: string;
        contentType: string;
        documentType: string;
        deliveredAt?: string;
        expiredAt?: string;
        createdAt?: string;
        updatedAt?: string;
    }[];
}

export interface PrivateSellerDetails {
    id: string;
    status: 'PENDING' | 'COMPLETED' | 'VERIFIED' | 'REJECTED';
    sellerType: 'private';
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    legalDocPath: string;
    paymentInfo: {
        id: string;
        paymentType: 'MOBILE_MONEY' | 'BANK';
        bankName: string | null;
        accountNumber: string | null;
        accountHolderName: string | null;
        mobileMoneyProvider: string | null;
        mobileMoneyNumber: string | null;
        numberVerified: boolean;
    };
    documents: {
        id: string;
        documentPath: string;
        contentType: string;
        documentType: string;
        deliveredAt?: string;
        expiredAt?: string;
        createdAt?: string;
        updatedAt?: string;
    }[];
}

export interface PrivateSellerDetails {
    id: string;
    status: 'PENDING' | 'COMPLETED' | 'VERIFIED' | 'REJECTED';
    sellerType: 'private';
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    legalDocPath: string;
    paymentInfo: {
        id: string;
        paymentType: 'MOBILE_MONEY' | 'BANK';
        bankName: string | null;
        accountNumber: string | null;
        accountHolderName: string | null;
        mobileMoneyProvider: string | null;
        mobileMoneyNumber: string | null;
        numberVerified: boolean;
    };
    documents: {
        id: string;
        documentPath: string;
        contentType: string;
        documentType: string;
        deliveredAt?: string;
        expiredAt?: string;
        createdAt?: string;
        updatedAt?: string;
    }[];
}

export type SellerDetails = CompanySellerDetails | PrivateSellerDetails;

@Injectable({
    providedIn: 'root'
})
export class AdminSellerService {
    constructor(private http: HttpClient) {}

    /**
     * Get list of all sellers
     */
    listSellers(): Observable<{ data: { data: SellerListItem[] } }> {
        return this.http.get<{ data: { data: SellerListItem[] } }>(API_URLS.ADMIN_LIST_SELLERS);
    }

    /**
     * Get detailed information for a specific seller
     */
    getSellerDetails(sellerId: string): Observable<{ data: SellerDetails }> {
        return this.http.get<{ data: SellerDetails }>(`${API_URLS.ADMIN_LIST_SELLERS}/${sellerId}`);
    }

    /**
     * Approve a seller account
     */
    approveSeller(sellerId: string): Observable<any> {
        return this.http.put(`${API_URLS.ADMIN_LIST_SELLERS}/${sellerId}/approveAccount`, {});
    }

    /**
     * Reject a seller account with reason
     */
    rejectSeller(sellerId: string, reason: string): Observable<any> {
        return this.http.put(`${API_URLS.ADMIN_LIST_SELLERS}/${sellerId}/rejectAccount`, { reason });
    }
}
